import { Link } from "react-router-dom";
import { Target, Eye, Heart, ShieldCheck, Users, Globe2, ArrowRight } from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import { Reveal, SectionHead } from "@/components/site/Reveal";
import { Button } from "@/components/ui/button";

const VALUES = [
  { icon: ShieldCheck, title: "Integrity first", desc: "We never fabricate results, reviews or guarantees. Honest expertise, always." },
  { icon: Heart, title: "Craft & care", desc: "Premium design and serious engineering in equal measure." },
  { icon: Users, title: "True partnership", desc: "Clear communication and a client portal that keeps you in the loop." },
  { icon: Globe2, title: "Worldwide mindset", desc: "Remote-first, working with clients across the globe." },
];

export default function About() {
  useSeo({ title: "About", description: "Buddy Solution is a worldwide, remote-first digital-solutions partner built on integrity, craft and real results." });
  return (
    <div>
      <section className="relative overflow-hidden border-b border-border">
        <div className="bs-aurora pointer-events-none absolute inset-0 opacity-60" />
        <div className="bs-container relative py-16 lg:py-24">
          <Reveal>
            <span className="bs-eyebrow">About Buddy Solution</span>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-black sm:text-5xl lg:text-6xl">
              A digital partner built on <span className="bs-gradient-text">trust and results</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
              Buddy Solution is a worldwide, remote-first team helping businesses design, build and grow with technology.
              We bring premium craft to every project, and we tell you the truth about what's possible.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bs-container grid gap-8 py-16 md:grid-cols-2 lg:py-20">
        <Reveal><div className="h-full bs-card p-8">
          <Target className="h-8 w-8 text-primary" />
          <h2 className="mt-4 font-display text-2xl font-bold">Our mission</h2>
          <p className="mt-3 text-muted-foreground">To make premium, results-driven digital solutions accessible to businesses everywhere, with technology that's secure, fast and genuinely useful.</p>
        </div></Reveal>
        <Reveal delay={0.08}><div className="h-full bs-card p-8">
          <Eye className="h-8 w-8 text-accent" />
          <h2 className="mt-4 font-display text-2xl font-bold">Our vision</h2>
          <p className="mt-3 text-muted-foreground">A world where every ambitious business, regardless of size or location, has a trustworthy technology partner that helps them grow.</p>
        </div></Reveal>
      </section>

      <section className="border-y border-border bg-card/30 py-16 lg:py-20">
        <div className="bs-container">
          <Reveal><SectionHead center eyebrow="Our values" title="What we stand for" /></Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.05}>
                <div className="h-full bs-card p-6">
                  <div className="mb-3 inline-flex rounded-xl bg-primary/12 p-2.5 text-primary"><v.icon className="h-5 w-5" /></div>
                  <h3 className="font-display text-base font-bold">{v.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bs-container py-16 text-center lg:py-20">
        <h2 className="font-display text-3xl font-extrabold">Let's build something great together</h2>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Button asChild className="rounded-full"><Link to="/contact">Start a Project <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/careers">Join our team</Link></Button>
        </div>
      </section>
    </div>
  );
}
